import React, { useState, useEffect } from 'react';
import { Eye, X, Clock } from 'lucide-react';

interface MimickedUser {
  id: string;
  name: string;
  email: string;
}

interface MimicUserBannerProps {
  mimickedUser: MimickedUser;
  onExit: () => void;
}

// Get token expiration time from JWT
const getTokenExpiration = (): number | null => {
  const token = localStorage.getItem('authToken');
  if (!token) return null;

  try {
    // Decode JWT payload (second part of the token)
    const payload = JSON.parse(atob(token.split('.')[1]));
    // exp is in seconds, convert to milliseconds
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

// Format time remaining
const formatTimeRemaining = (ms: number): string => {
  if (ms <= 0) return '0:00';

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const MimicUserBanner: React.FC<MimicUserBannerProps> = ({ mimickedUser, onExit }) => {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const expiration = getTokenExpiration();
    if (!expiration) return;

    const updateCountdown = () => {
      const remaining = expiration - Date.now();
      if (remaining <= 0) {
        setIsExpired(true);
        setTimeRemaining(0);
      } else {
        setTimeRemaining(remaining);
      }
    };

    // Initial update
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto-exit when expired
  useEffect(() => {
    if (isExpired) {
      // Give user a moment to see the expired state, then exit
      const timeout = setTimeout(() => {
        onExit();
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isExpired, onExit]);

  // Determine countdown color based on time remaining
  const getCountdownStyle = () => {
    if (timeRemaining === null) return 'bg-amber-600/30 text-amber-950';
    if (isExpired) return 'bg-red-600 text-white animate-pulse';
    if (timeRemaining < 5 * 60 * 1000) return 'bg-red-500/80 text-white'; // Less than 5 min
    if (timeRemaining < 15 * 60 * 1000) return 'bg-orange-500/80 text-white'; // Less than 15 min
    return 'bg-amber-600/30 text-amber-950';
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-amber-500 to-amber-400 text-amber-950 px-4 py-2 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            <span className="font-medium">
              Viewing as: <span className="font-bold">{mimickedUser.name}</span>
              <span className="text-amber-800 ml-2 hidden sm:inline">({mimickedUser.email})</span>
            </span>
          </div>

          {/* Countdown Timer */}
          {timeRemaining !== null && (
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-mono font-semibold transition-colors ${getCountdownStyle()}`}>
              <Clock className="w-3.5 h-3.5" />
              <span>{isExpired ? 'Expired' : formatTimeRemaining(timeRemaining)}</span>
            </div>
          )}
        </div>

        <button
          onClick={onExit}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded-md font-medium transition-colors shadow-sm"
        >
          <X className="w-4 h-4" />
          <span className="hidden sm:inline">Exit Mimic Mode</span>
          <span className="sm:hidden">Exit</span>
        </button>
      </div>
    </div>
  );
};
